import React, { useEffect } from 'react';

const HowWeWork = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="min-h-[60vh] flex items-center justify-center bg-[#F5F1ED] relative overflow-hidden">
        <div className="absolute left-20 top-1/4 w-[500px] h-[500px] border border-[#A08872]/10 rounded-full"></div>
        <div className="absolute left-32 top-1/3 w-[300px] h-[300px] border border-[#A08872]/10 rounded-full"></div>
        <div className="container mx-auto px-6 py-20 relative z-10">
          <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4 fade-on-scroll opacity-0">METHODOLOGY</p>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-[#2A2A2A] mb-6 fade-on-scroll opacity-0">How We Work</h1>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-[#2A2A2A] mb-8 md:mb-12 fade-on-scroll opacity-0">
            At UK Stage, we always start with execution.
          </h2>
          <div className="space-y-6 text-lg text-[#3D3D3D] leading-relaxed">
            <p className="fade-on-scroll opacity-0">
              It is the foundation upon which all great businesses are built. Through disciplined capital allocation and structured land aggregation, we work with our partners to build platforms that are truly best in class.
            </p>
            <p className="fade-on-scroll opacity-0">
              UK Stage specializes in identifying early-stage land opportunities, structuring acquisitions with pricing discipline, and unlocking value through development, repositioning, and strategic capital rotation.
            </p>
          </div>
        </div>
      </section>

      {/* Methodology Grid */}
      <section className="py-20 bg-[#EDE8E3]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#2A2A2A] mb-6">Disciplined Land Banking</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                We identify fragmented land parcels across emerging growth corridors and structure acquisitions with careful pricing discipline. Our hands-on operational approach enables us to convert complex land situations into viable assets.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#2A2A2A] mb-6">Structured Development</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                From transactional aggregation to development-led participation, we execute projects that create lasting value. Our portfolio includes luxury residential formats, managed assets, and lifestyle infrastructure.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#2A2A2A] mb-6">Strategic Trading</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Active capital rotation and strategic trading positions allow us to maintain liquidity and capital depth while scaling selectively into adjacent sectors. Real estate remains our foundation.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-3xl font-serif text-[#2A2A2A] mb-6">Capital Structuring</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                We operate through structured investment vehicles and selective strategic extensions that reinforce our core real estate capabilities, designed for institutional scalability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Break */}
      <section className="py-0">
        <img 
          src="https://images.unsplash.com/photo-1708173176426-7ccf24162426?w=1600&q=80" 
          alt="Uttarakhand Modern Development"
          className="w-full h-[220px] sm:h-[400px] md:h-[600px] object-cover fade-on-scroll opacity-0"
        />
      </section>

      {/* Approach Details */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-[#2A2A2A] mb-8 md:mb-12 fade-on-scroll opacity-0">Our Approach</h2>
          <div className="space-y-12">
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#8B7355] mb-4">Ground Intelligence</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Built as a first-generation enterprise, our platform combines execution-led ground intelligence with research-backed positioning and technology-enabled planning. Nearly two decades of experience have cultivated strong investor relationships across Northern India.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#8B7355] mb-4">Trust-Driven Execution</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                We have earned a reputation for trust-driven execution, structured dispute resolution, and long-term partnership orientation. Our early years were defined by on-ground negotiation and deep understanding of corridor evolution.
              </p>
            </div>
            
            <div className="fade-on-scroll opacity-0">
              <h3 className="text-2xl font-serif text-[#8B7355] mb-4">Long-Horizon Value Creation</h3>
              <p className="text-lg text-[#3D3D3D] leading-relaxed">
                Every venture is built on the stability of our real estate foundation. We prioritize tangible asset backing, capital protection through structured entry, execution-led value enhancement, and long-term compounding over short-term speculation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-32 bg-[#2A2A2A] text-white text-center">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif mb-6 fade-on-scroll opacity-0">
            Building Economic Platforms, Not Just Projects
          </h2>
          <p className="text-xl fade-on-scroll opacity-0">
            For investors and partners seeking asset-backed growth aligned with disciplined execution, UK Stage represents both stability and forward momentum.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;